import type { Role } from '../schemas/common.schema.js';
import { PEOPLE_MODULES } from './modules.people.js';
import { TIME_MODULES } from './modules.time.js';
import { OPERATIONS_MODULES } from './modules.operations.js';
import { INSIGHTS_MODULES } from './modules.insights.js';
import { SYSTEM_MODULES } from './modules.system.js';
import { SENSITIVE_MODULES } from './modules.sensitive.js';
import { permissionKey, type ModuleDef, type PermissionAction } from './types.js';

/**
 * The full catalog. One file per group so the modules can be extended without
 * every change landing in one place; this file only stitches them together and
 * derives the lookups — it declares no permission itself.
 */
export const PERMISSION_MODULES: readonly ModuleDef[] = [
  ...PEOPLE_MODULES,
  ...TIME_MODULES,
  ...OPERATIONS_MODULES,
  ...INSIGHTS_MODULES,
  ...SYSTEM_MODULES,
  ...SENSITIVE_MODULES,
];

export interface FlatPermission {
  key: string;
  module: string;
  action: PermissionAction;
  defaultRoles: readonly Role[];
  /** Affects the application itself — Super Administrator only by default. */
  platform: boolean;
}

export const ALL_PERMISSIONS: readonly FlatPermission[] = PERMISSION_MODULES.flatMap((m) =>
  m.permissions.map((p) => ({
    key: permissionKey(m.key, p.action),
    module: m.key,
    action: p.action,
    defaultRoles: p.defaultRoles,
    platform: p.platform === true || m.platform === true,
  }))
);

export const ALL_PERMISSION_KEYS: readonly string[] = ALL_PERMISSIONS.map((p) => p.key);
const KNOWN = new Set(ALL_PERMISSION_KEYS);

export function isPermissionKey(key: string): boolean {
  return KNOWN.has(key);
}

/** Every permission except the sensitive-information boxes, which nobody holds by default. */
export const ALL_NON_SENSITIVE_PERMISSION_KEYS: readonly string[] = ALL_PERMISSIONS.filter(
  (p) => !p.module.startsWith('sensitive.')
).map((p) => p.key);

export const isSensitivePermissionKey = (key: string): boolean => key.startsWith('sensitive.');

/** Permissions that change how the application itself runs — see PermissionDef.platform. */
export const PLATFORM_PERMISSION_KEYS: readonly string[] = ALL_PERMISSIONS.filter((p) => p.platform).map((p) => p.key);
const PLATFORM = new Set(PLATFORM_PERMISSION_KEYS);
export const isPlatformPermissionKey = (key: string): boolean => PLATFORM.has(key);

/**
 * THE FIVE BUILT-IN TIERS. The database enum stays ADMIN / HR / MANAGER /
 * EMPLOYEE; two built-in access roles share the base role ADMIN.
 *
 *  SUPER_ADMIN  every non-sensitive permission, and it cannot be narrowed. It
 *               alone manages the application itself. A user with role ADMIN and
 *               NO access role IS a Super Administrator — which is what every
 *               admin that existed before the split, and the installer's first
 *               admin, already are.
 *  ADMIN        the Administrator: the same, minus everything platform. Company
 *               management. Held only through an explicit assignment.
 *  HR / MANAGER / EMPLOYEE  their `defaultRoles`, never a platform permission.
 *
 * Sensitive information (bank, identity, salary) is in NONE of them: it is
 * granted per person or per role, whatever the tier.
 */
export const SYSTEM_ROLE_KEYS = ['SUPER_ADMIN', 'ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] as const;
export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number];

export function defaultPermissionsForSystemRole(key: string): string[] {
  if (key === 'SUPER_ADMIN') return [...ALL_NON_SENSITIVE_PERMISSION_KEYS];
  if (key === 'ADMIN') return ALL_NON_SENSITIVE_PERMISSION_KEYS.filter((k) => !PLATFORM.has(k));
  if (key !== 'HR' && key !== 'MANAGER' && key !== 'EMPLOYEE') return [];
  return ALL_PERMISSIONS.filter((p) => !p.platform && p.defaultRoles.includes(key)).map((p) => p.key);
}

/**
 * What a base role holds out of the box when it has no access role. For ADMIN
 * that is the SUPER ADMINISTRATOR (role ADMIN + no access role = Super
 * Administrator), so a hand-built user with role ADMIN keeps full power. The
 * company Administrator is `defaultPermissionsForSystemRole('ADMIN')`.
 */
export function defaultPermissionsForRole(role: Role): string[] {
  return defaultPermissionsForSystemRole(role === 'ADMIN' ? 'SUPER_ADMIN' : role);
}

/** Whether `held` covers everything a Super Administrator holds — the test for "may hand out or remove that tier". */
export function holdsSuperAdminPermissions(held: { has(key: string): boolean }): boolean {
  return ALL_NON_SENSITIVE_PERMISSION_KEYS.every((k) => held.has(k));
}

/** A stored key that was split: the old `settings.view` / `settings.edit` boxes. Never the app-side box. */
const LEGACY_ALIASES: Readonly<Record<string, readonly string[]>> = {
  'settings.view': ['settings.company.view', 'settings.workforce.view'],
  'settings.edit': ['settings.company.edit', 'settings.workforce.edit'],
};

/** Drop anything the catalog does not know — a stored key outlives the code that named it. */
export function sanitizePermissions(keys: readonly string[]): string[] {
  const expanded = keys.flatMap((k) => LEGACY_ALIASES[k] ?? [k]);
  return [...new Set(expanded.filter((k) => KNOWN.has(k)))].sort();
}
